import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { ChartDataLabel } from 'locale/es'
import { ParametersTypes } from 'resources/ParametersTypes'
import PatientsService from 'services/PatientsService'
import {
  convertToLocalTime,
  createBarChartObject,
  filterAndSortDates,
  filterAndSortSpecificDates,
  getTotalValuesPerWeek,
  getDaysArrayByMonth,
  getDaysBySelectedRange,
  getHoursArray,
  getMonthWeeksArray,
  createDayAndValuesDict,
  getTotalValuesPerHour,
  getWeekDaysByWeekNumber
} from 'utils/HelperFunctions'
import moment from 'moment'
import { addToCache } from './cacheReducer'


export const fetchStepsDataIfNeeded = createAsyncThunk(
  'stepsStats/fetchStepsDataIfNeeded',
  async ({ patientId, selectedDate, type, name, aggregation }, { getState, dispatch }) => {
    const startDate = type === 'STEPS_HOUR_IN_DAY'
      ? moment(selectedDate).subtract(1, 'months').startOf('month').format("YYYY-MM-DDTHH:mm:ss[Z]")
      : moment(selectedDate).subtract(2, 'months').startOf('month').format("YYYY-MM-DDTHH:mm:ss[Z]");
    const endDate = moment(selectedDate).endOf('month').format(("YYYY-MM-DDTHH:mm:ss[Z]"));
    const key = `stepsData-${type}-${patientId}`;

    const cache = getState().cache.apiResponses[key];
    const selectedMoment = moment(selectedDate);

    if (cache && selectedMoment.isBetween(moment(cache.startDate), moment(cache.endDate), undefined, '[]')) {
      dispatch(updateStateWithCachedData({ type, data: cache.data, status: 'cached' }));
      return cache.data;
    } else {
      const { data, status } = await PatientsService.getObservations(name, patientId, startDate, endDate, aggregation);
      if (status === 200) {
        const existingData = cache ? cache.data : [];
        const combinedData = [...existingData, ...data];
        dispatch(addToCache({ key, data: combinedData, startDate: cache ? cache.startDate : startDate, endDate }));
        return { data: combinedData, type, status };
      }
    }
  }
)

const initialState = {
  lastThreeWeeksPatientData: {},
  lastTwoMonthsPatientData: {},
  lastTwoMonthsPerDayInMonthPatientData: {},
  lastTwoMonthsPerHourInDayPatientData: {},
  lastSixMonthsPatientData: {},
  lastYearsPatientData: {},
  perHourChartTotalStepsInDay: {},
  perDayChartTotalStepsInMonth: {},
  perWeekChartAvgStepsInMonth: {},
}

const stepsStatsSlice = createSlice({
  name: 'stepsStats',
  initialState,
  extraReducers: (builder) => {
    builder.addCase(fetchStepsDataIfNeeded.fulfilled, (state, action) => {
      const { type, data, status } = action.payload;
      switch (type) {
        case "STEPS_AVERAGE":
          state.lastTwoMonthsPatientData = { data, status };
          break;
        case "STEPS_DAY_IN_MONTH":
          state.lastTwoMonthsPerDayInMonthPatientData = { data, status };
          break;
        case "STEPS_HOUR_IN_DAY":
          state.lastTwoMonthsPerHourInDayPatientData = { data, status };
          break;
        default:
          break;
      }
    })
  },
  reducers: {
    updateStateWithCachedData(state, action) {
      const { type, data, status } = action.payload;
      switch (type) {
        case "STEPS_AVERAGE":
          state.lastTwoMonthsPatientData = { data, status };
          break;
        case "STEPS_DAY_IN_MONTH":
          state.lastTwoMonthsPerDayInMonthPatientData = { data, status };
          break;
        case "STEPS_HOUR_IN_DAY":
          state.lastTwoMonthsPerHourInDayPatientData = { data, status };
          break;
        default:
          break;
      }
    },
    setPatientStatsLastThreeWeeksPerHour(state, action) {
      return { ...state, lastThreeWeeksPatientData: action.payload }
    },
    setPatientStatsLastTwoMonthsPerDayInMonth(state, action) {
      return { ...state, lastTwoMonthsPerDayInMonthPatientData: action.payload }
    },
    setPatientStatsLastTwoMonthsPerHourInDay(state, action) {
      return { ...state, lastTwoMonthsPerHourInDayPatientData: action.payload }
    },
    setPatientStatsLastYearPerDay(state, action) {
      return { ...state, lastYearsPatientData: action.payload }
    },
    setStepsStatsLastSixMonthsPerMonth(state, action) {
      return { ...state, lastSixMonthsPatientData: action.payload }
    },
    setStepsStatsLasYearsPerHour(state, action) {
      return { ...state, lastYearsPatientData: action.payload }
    },
    setChartTotalStepsInDay(state, action) {
      const selectedDate = action.payload
      const { data } = state.lastTwoMonthsPerHourInDayPatientData

      const dateFormat = "H";

      let valuesToDisplay

      try {
        const dataConvertedToLocalTime = convertToLocalTime(data)

        const stepsStatsSelectedDaySortedPerHour = filterAndSortDates(dataConvertedToLocalTime, selectedDate, 'day')

        const valuesPerDay = getTotalValuesPerHour(getHoursArray(), stepsStatsSelectedDaySortedPerHour, dateFormat)

        valuesToDisplay = Object.values(valuesPerDay).map(dataPoint => dataPoint)
      } catch {
        valuesToDisplay = []
      }

      const labels = getHoursArray().map(hour => hour.length === 1 ? `0${hour}` : hour)
      const dataLabel = ChartDataLabel.TOTAL_STEPS

      const chartObject = createBarChartObject(valuesToDisplay, dataLabel, labels)

      return { ...state, perHourChartTotalStepsInDay: chartObject }
    },
    setChartTotalStepsInMonth(state, action) {
      const selectedDate = action.payload
      const { data } = state.lastTwoMonthsPerDayInMonthPatientData

      const dateFormat = "DD/MM/YYYY";

      let valuesToDisplay

      try {
        const stepsStatsSelectedMonthSortedPerDay = filterAndSortDates(data, selectedDate, 'month')
        const valuesPerDay = createDayAndValuesDict(getDaysArrayByMonth(selectedDate), stepsStatsSelectedMonthSortedPerDay, dateFormat)

        valuesToDisplay = Object.values(valuesPerDay).map(dataPoint => dataPoint)
      } catch {
        valuesToDisplay = []
      }

      const labels = getDaysArrayByMonth(selectedDate)
      const dataLabel = ChartDataLabel.TOTAL_STEPS

      const chartObject = createBarChartObject(valuesToDisplay, dataLabel, labels)

      return { ...state, perDayChartTotalStepsInMonth: chartObject }
    },
    setChartAvgStepsInMonth(state, action) {
      const selectedDate = action.payload
      const { data } = state.lastTwoMonthsPatientData

      const dateFormat = "DD/MM/YYYY";

      const dataLabel = ChartDataLabel.AVERAGE_STEPS
      let valuesToDisplay
      let labels

      try {
        // TODO: Only firstDay and lastDay are needed. Refactor getting those values into a function and get rid of the fluff
        const firstDayOfMonth = moment(selectedDate).startOf('month')
        const lastDayOfMonth = moment(selectedDate).endOf('month')

        // ! getWeekDaysByWeekNumber is the main fluff
        const daysOfFirstWeek = getWeekDaysByWeekNumber(firstDayOfMonth)
        const daysOfLastWeek = getWeekDaysByWeekNumber(lastDayOfMonth)

        const firstDay = moment(daysOfFirstWeek[0], dateFormat)
        const lastDay = moment(daysOfLastWeek.at(-1), dateFormat)

        const stepsStatsSelectedMonthSortedPerDay = filterAndSortSpecificDates(data, firstDay, lastDay)

        // TODO: Check that total steps in month chart works properly (probably I just fixed the same bug that was here)
        // TODO: Check convertToLocalTime function behaviour in all charts that use total values in a day
        const valuesPerDay = createDayAndValuesDict(getDaysBySelectedRange(firstDay, lastDay), stepsStatsSelectedMonthSortedPerDay, dateFormat)

        const valuesPerWeek = getTotalValuesPerWeek(valuesPerDay, getMonthWeeksArray(firstDay, lastDay), dateFormat)

        valuesToDisplay = Object.values(valuesPerWeek).map(weekData => (weekData / 7).toFixed(1))
        labels = getMonthWeeksArray(firstDay, lastDay)
      } catch {
        valuesToDisplay = []
      }
      const chartObject = createBarChartObject(valuesToDisplay, dataLabel, labels)

      return { ...state, perWeekChartAvgStepsInMonth: chartObject }
    },
    resetStepsState(state, action) {
      return initialState
    }
  },

})

export const {
  setPatientStatsLastThreeWeeksPerHour,
  setPatientStatsLastTwoMonthsPerDay,
  setStepsStatsLastSixMonthsPerMonth,
  setStepsStatsLasYearsPerHour,
  setChartTotalStepsInDay,
  setChartTotalStepsInMonth,
  setChartAvgStepsInMonth,
  setPatientStatsLastYearPerDay,
  setPatientStatsLastTwoMonthsPerDayInMonth,
  setPatientStatsLastTwoMonthsPerHourInDay,
  updateStateWithCachedData,
  resetStepsState
} =
  stepsStatsSlice.actions

export const getStepsStatsAverageInDay = (patientId, selectedDate) => {
  return async (dispatch, getState) => {
    const { name, aggregation, type } = ParametersTypes.STEPS_AVERAGE;
    const params = {
      patientId,
      selectedDate,
      type,
      name,
      aggregation
    }
    dispatch(fetchStepsDataIfNeeded(params));

  }
}

export const getStepsStatsLasTwoMonthsPerDay = (patientId, selectedDate) => {
  return async (dispatch, getState) => {
    const { name, aggregation, type } = ParametersTypes.STEPS_DAY_IN_MONTH;
    const params = {
      patientId,
      selectedDate,
      type,
      name,
      aggregation
    }
    dispatch(fetchStepsDataIfNeeded(params));

  }
}

export const getStepsStatsLasTwoMonthsPerHour = (patientId, selectedDate) => {
  return async (dispatch, getState) => {

    const { name, aggregation, type } = ParametersTypes.STEPS_HOUR_IN_DAY;
    const params = {
      patientId,
      selectedDate,
      type,
      name,
      aggregation
    }
    dispatch(fetchStepsDataIfNeeded(params));
  }
}

export const changeDateLastTwoMonthsChart = (selectedDate) => {
  return (dispatch) => {
    dispatch(setChartAvgStepsInMonth(selectedDate))
  }
}

export const changeDateLastTwoMonthPerDayInMonthChart = (selectedDate) => {
  return (dispatch) => {
    dispatch(setChartTotalStepsInMonth(selectedDate))
  }
}

export const changeDateLastTwoMonthHourInDayChart = (selectedDate) => {
  return (dispatch) => {
    dispatch(setChartTotalStepsInDay(selectedDate))
  }
}

export default stepsStatsSlice.reducer